import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  FileButton,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconStar, IconStarFilled, IconTrash, IconUpload } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import {
  ITEM_IMAGE_MAX_BYTES,
  listItemImages,
  makeItemImageDefault,
  removeItemImage,
  uploadItemImage,
  type ItemImageDto,
} from './api';
import { ItemPhotoThumbnail } from './ItemPhotoThumbnail';

interface Props {
  itemId: string;
  /** False while the operator has no write permission — the list stays readable. */
  canWrite: boolean;
}

/**
 * An item's photos: upload, choose which one represents the item, remove.
 *
 * Only rendered while EDITING. A photo needs an item to belong to, and the item does not exist
 * until the form is saved — offering the control on a new item would mean holding files in memory
 * and posting them afterwards, with a half-saved state if the second step failed.
 */
export function ItemPhotos({ itemId, canWrite }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  const photos = useQuery({
    queryKey: ['item-images', itemId] as const,
    queryFn: ({ signal }) => listItemImages(itemId, signal),
  });

  /** Every write returns the item's full list, so they all settle the same way. */
  function settle(rows: ItemImageDto[]) {
    queryClient.setQueryData(['item-images', itemId], rows);
    // The item list shows the default photo, so it has to hear about a change of default.
    void queryClient.invalidateQueries({ queryKey: ['items'] });
    setError(null);
  }

  function fail(e: unknown) {
    // Both detail and title are optional on a Problem Details document, so neither can be trusted
    // to be there — falling through to our own wording beats rendering "undefined".
    const problem = e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : null;
    setError(problem ?? t('items.photos.failed'));
  }

  const upload = useMutation({
    mutationFn: (file: File) => uploadItemImage(itemId, file),
    onSuccess: settle,
    onError: fail,
  });

  const promote = useMutation({
    mutationFn: (imageId: string) => makeItemImageDefault(itemId, imageId),
    onSuccess: settle,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (imageId: string) => removeItemImage(itemId, imageId),
    onSuccess: settle,
    onError: fail,
  });

  const busy = upload.isPending || promote.isPending || remove.isPending;
  const rows = photos.data ?? [];

  function choose(file: File | null) {
    // FileButton keeps the chosen file until it is reset, so picking the same file twice in a row
    // would otherwise do nothing the second time.
    resetRef.current?.();
    if (!file) return;

    // Checked here as well as on the server, so a phone photo that is too big fails instantly
    // instead of after uploading several megabytes over a warehouse's connection.
    if (file.size > ITEM_IMAGE_MAX_BYTES) {
      setError(t('items.photos.tooLarge', { max: ITEM_IMAGE_MAX_BYTES / 1024 / 1024 }));
      return;
    }
    upload.mutate(file);
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={500}>
          {t('items.photos.section')}
        </Text>
        {canWrite && (
          <FileButton resetRef={resetRef} onChange={choose} accept="image/jpeg,image/png,image/gif,image/webp">
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={upload.isPending}
                disabled={busy}
              >
                {t('items.photos.add')}
              </Button>
            )}
          </FileButton>
        )}
      </Group>

      <Text size="xs" c="dimmed">
        {t('items.photos.hint')}
      </Text>

      {error && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {photos.isLoading && (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      )}

      {!photos.isLoading && rows.length === 0 && (
        <Card withBorder padding="md">
          <Text size="sm" c="dimmed" ta="center">
            {t('items.photos.none')}
          </Text>
        </Card>
      )}

      {rows.length > 0 && (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
          {rows.map((p) => (
            <Card key={p.itemImageId} withBorder padding="xs">
              <Card.Section>
                <ItemPhotoThumbnail itemId={itemId} image={p} height={110} />
              </Card.Section>

              <Group justify="space-between" mt="xs" wrap="nowrap" gap={4}>
                {p.isDefault ? (
                  <Badge size="xs" variant="light" leftSection={<IconStarFilled size={10} />}>
                    {t('items.photos.default')}
                  </Badge>
                ) : (
                  <Tooltip label={t('items.photos.makeDefault')} withArrow>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      aria-label={t('items.photos.makeDefaultFor', { name: p.fileName ?? '' })}
                      disabled={!canWrite || busy}
                      onClick={() => promote.mutate(p.itemImageId)}
                    >
                      <IconStar size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}

                <Tooltip label={t('items.photos.remove')} withArrow>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    aria-label={t('items.photos.removeFor', { name: p.fileName ?? '' })}
                    disabled={!canWrite || busy}
                    onClick={() => remove.mutate(p.itemImageId)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
